import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card, Skeleton } from '@heroui/react';
import { ROUTES } from '@/shared/config';
import { PageLayout } from '@/shared/ui';

/**
 * Экраны на время загрузки чанка (`lazyPage` в `app/router`): те же шапки и
 * скелеты, что страницы показывают, пока ждут настройки, — загрузка кода и
 * данных идёт одним шиммером.
 */
export function PersonalizationPageSkeleton() {
  const { t } = useTranslation('PersonalizationPage');
  const navigate = useNavigate();
  return (
    <PageLayout title={t('title')} onBack={() => void navigate(ROUTES.profile)}>
      <PersonalizationSkeleton />
    </PageLayout>
  );
}

export function PersonalizationInterestsPageSkeleton() {
  const { t } = useTranslation('PersonalizationInterestsPage');
  const navigate = useNavigate();
  return (
    <PageLayout title={t('title')} onBack={() => void navigate(-1)}>
      <InterestsSkeleton label={t('loading')} />
    </PageLayout>
  );
}

export function PersonalizationGoalPageSkeleton() {
  const { t } = useTranslation('PersonalizationGoalPage');
  const navigate = useNavigate();
  return (
    <PageLayout title={t('title')} onBack={() => void navigate(-1)}>
      <GoalSkeleton label={t('loading')} />
    </PageLayout>
  );
}

export function PersonalizationSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true" aria-live="polite">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-[60px] rounded-2xl" />
        <Skeleton className="h-3 w-4/5 rounded-full" />
      </div>
      <Card className="flex flex-col gap-0 divide-y divide-hairline overflow-hidden p-0">
        <Skeleton className="h-[60px] rounded-none" />
        <Skeleton className="h-[60px] rounded-none" />
        <Skeleton className="h-[60px] rounded-none" />
      </Card>
    </div>
  );
}

/** Ширины пилюль — по паре строк на группу, чтобы силуэт был похож на настоящее облако чипов. */
const SKELETON_GROUPS: readonly (readonly string[])[] = [
  ['w-16', 'w-20', 'w-14', 'w-24', 'w-16', 'w-20', 'w-16'],
  ['w-16', 'w-24', 'w-20', 'w-16', 'w-20', 'w-14'],
  ['w-20', 'w-16', 'w-16', 'w-24', 'w-14'],
];

export function InterestsSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {SKELETON_GROUPS.map((widths, group) => (
        <div key={group} className="flex flex-col gap-2">
          <Skeleton className="h-3 w-28 rounded-full" />
          <div className="flex flex-wrap gap-2">
            {widths.map((width, chip) => (
              <Skeleton key={chip} className={`h-10 ${width} rounded-full`} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Столько же карточек, сколько вариантов в `GOALS`. */
const GOAL_SKELETON_ROWS = 6;

export function GoalSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {Array.from({ length: GOAL_SKELETON_ROWS }, (_, row) => (
        <Skeleton key={row} className="h-16 rounded-2xl" />
      ))}
    </div>
  );
}
